import { Component, OnInit, OnDestroy, ViewChild, inject, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DateFormatPipe, StatusBadgePipe } from '../../../../shared/pipes';
import { EmailReportModalComponent } from '../../../../shared/components/email-report-modal/email-report-modal.component';
import { FaaReport, FaaReportStatus, CreateFaaReportRequest } from '../../../../core/models/faa-report.model';
import { FaaReportService } from '../../../../core/services/faa-report.service';
import { OrganizationContextService } from '../../../../core/services/organization-context.service';
import { ToastService } from '../../../../core/services/toast.service';

/**
 * FAA Reports Component
 *
 * Displays and manages FAA compliance reports for the organization.
 * Integrates with real backend API via FaaReportService and receives
 * real-time progress updates via WebSocket.
 *
 * Reference: /agent-os/specs/2026-01-15-faa-reports/spec.md
 */
@Component({
  selector: 'app-faa-reports',
  standalone: true,
  imports: [CommonModule, FormsModule, DateFormatPipe, StatusBadgePipe, EmailReportModalComponent],
  templateUrl: './faa-reports.component.html',
  styleUrls: ['./faa-reports.component.scss']
})
export class FaaReportsComponent implements OnInit, OnDestroy {
  // Inject services
  private faaReportService = inject(FaaReportService);
  private organizationContext = inject(OrganizationContextService);
  private toastService = inject(ToastService);

  // Shared email modal (drives its own form state; we drive its async feedback)
  @ViewChild(EmailReportModalComponent) emailModal?: EmailReportModalComponent;

  // Signal-based state from service (bound to real API data)
  readonly recentReports = this.faaReportService.reports;
  readonly isLoading = this.faaReportService.isLoading;
  readonly error = this.faaReportService.error;
  readonly reportTypes = this.faaReportService.reportTypes;

  // Local UI state signals
  isGenerating = signal(false);
  deleteError = signal<string | null>(null);

  // Form fields (local state)
  startDate: string = '';
  endDate: string = '';
  reportType: string = 'complete_faa';
  includeFlightLogs: boolean = true;
  includeSprayRecords: boolean = true;
  includePilotInfo: boolean = true;
  includeAircraftInfo: boolean = true;

  // Preview modal state
  showPreviewModal = signal(false);
  previewReport = signal<FaaReport | null>(null);
  previewLoading = signal(false);

  // Email modal state (which report; the modal owns the rest)
  showEmailModal = signal(false);
  emailReport = signal<FaaReport | null>(null);

  // Delete confirmation state
  showDeleteConfirm = signal(false);
  reportToDelete = signal<FaaReport | null>(null);
  deleteLoading = signal(false);

  ngOnInit(): void {
    this.setDefaultDates();
    this.loadReports();
  }

  ngOnDestroy(): void {
    // Service cleanup is handled by the service's OnDestroy
  }

  /**
   * Load reports from API
   */
  private async loadReports(): Promise<void> {
    const orgId = this.organizationContext.currentOrganizationId();
    if (!orgId) {
      console.warn('No organization ID available, cannot load FAA reports');
      return;
    }

    await this.faaReportService.loadReports(orgId);
  }

  /**
   * Clear error state
   */
  clearError(): void {
    this.faaReportService.clearError();
  }

  /**
   * Retry loading reports after an error
   */
  retryLoad(): void {
    this.clearError();
    this.loadReports();
  }

  /**
   * Set default date range (last 30 days)
   */
  setDefaultDates(): void {
    const today = new Date();
    const thirtyDaysAgo = new Date(today);
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

    this.endDate = today.toISOString().split('T')[0];
    this.startDate = thirtyDaysAgo.toISOString().split('T')[0];
  }

  /**
   * Reset the generate report form to defaults
   */
  resetForm(): void {
    this.setDefaultDates();
    this.reportType = 'complete_faa';
    this.includeFlightLogs = true;
    this.includeSprayRecords = true;
    this.includePilotInfo = true;
    this.includeAircraftInfo = true;
  }

  /**
   * Generate a new report
   * Creates a draft report and immediately triggers PDF generation
   */
  async generateReport(): Promise<void> {
    const orgId = this.organizationContext.currentOrganizationId();
    if (!orgId) {
      console.error('No organization ID available');
      return;
    }

    // FAA §F.2.d — warn before filing, never block.
    //
    // Records with no flight count entered are filed as 1. On a busy month
    // that silently understates the filing by a large margin, and today the
    // operator has no way to notice until the PDF exists. Surfacing it here is
    // the only place the information can still change what gets filed.
    //
    // Deliberately advisory: an under-count is a compliance problem, but being
    // unable to file at all is worse. A failed or unavailable check returns
    // null and generation proceeds untouched.
    const missing = await this.faaReportService.fetchMissingFlightCounts(
      orgId,
      this.startDate,
      this.endDate
    );

    if (missing && missing.recordsMissingCount > 0) {
      const { recordsMissingCount: n, totalRecords: m, flightsMissingCount } = missing;
      const proceed = window.confirm(
        `${n} of ${m} records have no flight count entered.\n\n` +
        `${flightsMissingCount} aircraft will each be filed as 1 flight, ` +
        `which may understate what was actually flown.\n\n` +
        `Click Cancel to enter the counts first, or OK to generate anyway.`
      );

      if (!proceed) {
        return;
      }
    }

    this.isGenerating.set(true);

    try {
      // Create the report request
      const request: CreateFaaReportRequest = {
        name: this.getReportName(),
        startDate: this.startDate,
        endDate: this.endDate,
        options: {
          includeFlightLogs: this.includeFlightLogs,
          includeSprayRecords: this.includeSprayRecords,
          includePilotInfo: this.includePilotInfo,
          includeAircraftInfo: this.includeAircraftInfo
        }
      };

      // Create draft report via API
      const newReport = await this.faaReportService.createAndAddReport(orgId, request);

      if (newReport) {
        // Immediately trigger PDF generation
        await this.faaReportService.triggerPdfGeneration(orgId, newReport.id);
        // Reset form after successful generation start
        this.resetForm();
      }
    } catch (error) {
      console.error('Error generating report:', error);
    } finally {
      this.isGenerating.set(false);
    }
  }

  /**
   * Generate PDF for an existing draft report
   */
  async generatePdf(report: FaaReport): Promise<void> {
    const orgId = this.organizationContext.currentOrganizationId();
    if (!orgId) return;

    if (report.status !== 'draft') return;

    await this.faaReportService.triggerPdfGeneration(orgId, report.id);
  }

  /**
   * Download report PDF
   * Gets fresh pre-signed URL and opens in new tab
   */
  async downloadReport(report: FaaReport): Promise<void> {
    if (report.status !== 'ready' && report.status !== 'submitted') return;

    const orgId = this.organizationContext.currentOrganizationId();
    if (!orgId) return;

    try {
      // Fetch fresh report data to get a valid pre-signed URL
      const freshReport = await this.faaReportService.fetchReport(orgId, report.id);

      // Use downloadUrl (pre-signed URL) for download
      if (freshReport?.downloadUrl) {
        window.open(freshReport.downloadUrl, '_blank');
      } else {
        console.error('No download URL available');
      }
    } catch (error) {
      console.error('Error fetching report for download:', error);
    }
  }

  /**
   * View report in preview modal
   */
  viewReport(report: FaaReport): void {
    this.previewReport.set(report);
    this.showPreviewModal.set(true);
    this.previewLoading.set(true);

    // Load fresh report data for preview
    const orgId = this.organizationContext.currentOrganizationId();
    if (orgId) {
      this.faaReportService.fetchReport(orgId, report.id).then(freshReport => {
        if (freshReport) {
          this.previewReport.set(freshReport);
        }
        this.previewLoading.set(false);
      }).catch(() => {
        this.previewLoading.set(false);
      });
    } else {
      this.previewLoading.set(false);
    }
  }

  closePreviewModal(): void {
    this.showPreviewModal.set(false);
    this.previewReport.set(null);
  }

  /**
   * Open the agency-submission modal.
   *
   * Allowed for `submitted` reports too, so a re-send is possible — nothing was actually sent
   * by ADN, so "already submitted" is the user's own assertion and must not lock them out.
   */
  openEmailModal(report: FaaReport): void {
    if (report.status !== 'ready' && report.status !== 'submitted') return;
    this.emailReport.set(report);
    this.showEmailModal.set(true);
  }

  closeEmailModal(): void {
    this.showEmailModal.set(false);
    this.emailReport.set(null);
  }

  /**
   * Report name shown in the email modal
   */
  emailReportTitle(): string {
    const report = this.emailReport();
    if (!report) return '';
    return report.name || this.getReportTypeLabel(report.reportType || '');
  }

  /**
   * Report date range shown in the email modal
   */
  emailReportSubtitle(): string {
    const report = this.emailReport();
    if (!report) return '';
    return this.formatDateRange(report.startDate, report.endDate);
  }

  /**
   * Download the report the email modal is composing for.
   * Reuses the existing fresh-presigned-URL flow unchanged.
   */
  onEmailDownloadRequested(): void {
    const report = this.emailReport();
    if (!report) return;
    void this.downloadReport(report);
  }

  /**
   * Record the report as submitted to the chosen recipient.
   *
   * ADN sends nothing — the user sends the message from their own mail client and then tells us.
   * This hits the existing `POST /:id/submit` endpoint unchanged; `submitted_to` already stores a
   * recipient address, which is precisely what we need it to do.
   */
  async onEmailSubmitted(email: string): Promise<void> {
    const report = this.emailReport();
    if (!report) return;

    const orgId = this.organizationContext.currentOrganizationId();
    if (!orgId) return;

    this.emailModal?.setProcessing(true);
    this.emailModal?.setError(null);

    try {
      const success = await this.faaReportService.submitAndUpdateReport(orgId, report.id, email);
      if (success) {
        this.closeEmailModal();
        this.toastService.success(`Report marked as submitted to ${email}.`);
      } else {
        this.emailModal?.setError('Failed to submit report. Please try again.');
      }
    } catch (error: any) {
      console.error('Error submitting report:', error);
      this.emailModal?.setError(error.message || 'Failed to submit report. Please try again.');
    } finally {
      this.emailModal?.setProcessing(false);
    }
  }

  /**
   * Open delete confirmation modal
   */
  confirmDelete(report: FaaReport): void {
    this.deleteError.set(null);
    this.reportToDelete.set(report);
    this.showDeleteConfirm.set(true);
  }

  cancelDelete(): void {
    this.showDeleteConfirm.set(false);
    this.reportToDelete.set(null);
    this.deleteError.set(null);
    this.deleteLoading.set(false);
  }

  /**
   * Delete report
   */
  async deleteReport(): Promise<void> {
    const report = this.reportToDelete();
    if (!report) return;

    const orgId = this.organizationContext.currentOrganizationId();
    if (!orgId) return;

    // Cannot delete while generating
    if (report.status === 'generating') {
      this.deleteError.set('Cannot delete a report while it is generating');
      return;
    }

    this.deleteLoading.set(true);
    this.deleteError.set(null);

    try {
      const success = await this.faaReportService.deleteAndRemoveReport(orgId, report.id);
      if (success) {
        this.cancelDelete();
      } else {
        this.deleteError.set('Failed to delete report');
      }
    } catch (error: any) {
      console.error('Error deleting report:', error);
      // Handle 400 error for generating status
      if (error.status === 400 || error.message?.includes('generating')) {
        this.deleteError.set('Cannot delete a report while it is generating');
      } else {
        this.deleteError.set(error.message || 'Failed to delete report');
      }
    } finally {
      this.deleteLoading.set(false);
    }
  }

  /**
   * Retry a failed report
   */
  async retryReport(report: FaaReport): Promise<void> {
    const orgId = this.organizationContext.currentOrganizationId();
    if (!orgId) return;

    if (report.status !== 'failed') return;

    await this.faaReportService.retryFailedReport(orgId, report.id);
  }

  /**
   * Get human-readable status text
   */
  getStatusText(status: string): string {
    return this.faaReportService.getStatusText(status as FaaReportStatus);
  }

  /**
   * Get Bootstrap icon class for status
   */
  getStatusIcon(status: string): string {
    return this.faaReportService.getStatusIcon(status as FaaReportStatus);
  }

  /**
   * Get report type label from value
   */
  getReportTypeLabel(value: string): string {
    return this.faaReportService.getReportTypeLabel(value);
  }

  /**
   * Format date range for display
   */
  formatDateRange(startDate: string, endDate: string): string {
    return this.faaReportService.formatDateRange(startDate, endDate);
  }

  /**
   * Generate report name based on type and date range
   */
  private getReportName(): string {
    const typeLabel = this.getReportTypeLabel(this.reportType);
    const startMonth = new Date(this.startDate).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
    const endMonth = new Date(this.endDate).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });

    if (startMonth === endMonth) {
      return `${typeLabel} - ${startMonth}`;
    }
    return `${typeLabel} - ${startMonth} to ${endMonth}`;
  }
}
